/**
 * Shared care-time state for call-based clinical documentation.
 *
 * Owns the minutes value plus the optional linked Dialpad call (selected via
 * `DialpadCallPicker`). Replaces the per-form `careMinutes` boilerplate and the
 * earlier silent "most recent call" prefill: minutes are only ever set by the
 * clinician typing or by explicitly selecting a call, and the linked call's
 * `Communication` id + period are exposed for the care-time write so the entry
 * is auditable.
 *
 * @agentic connections
 */

import { useCallback, useRef, useState } from "react";
import type { RecentCall } from "@/hooks/domain/useRecentPatientCalls";

/** Result of {@link useCallTimeCareSelection}. @agentic connections */
export type UseCallTimeCareSelectionResult = {
  /** Current minutes value (undefined until entered or a call is selected). */
  minutes: number | undefined;
  /** Manual minutes setter (keeps any linked call). */
  setMinutes: (value: number | undefined) => void;
  /** Currently linked call, if one was selected. */
  selectedCall: RecentCall | undefined;
  /** Select a call (sets minutes), or pass `undefined` to clear the link. */
  selectCall: (call: RecentCall | undefined) => void;
  /** Stable accessor for the latest minutes, for the submit handler. */
  getCareTimeMinutes: () => number | undefined;
  /** Stable accessor for the linked call's Communication id, for traceability. */
  getCareTimeCommunicationId: () => string | undefined;
  /** Stable accessor for the linked call's effective period. */
  getCareTimeEffectivePeriod: () => { start: string; end: string } | undefined;
};

/**
 * Own minutes + linked-call state for a call-documentation surface.
 * @agentic connections
 */
export const useCallTimeCareSelection = (): UseCallTimeCareSelectionResult => {
  const [minutes, setMinutesState] = useState<number | undefined>(undefined);
  const [selectedCall, setSelectedCallState] = useState<RecentCall | undefined>(
    undefined,
  );
  const minutesRef = useRef<number | undefined>(undefined);
  const selectedRef = useRef<RecentCall | undefined>(undefined);

  const setMinutes = useCallback((value: number | undefined) => {
    minutesRef.current = value;
    setMinutesState(value);
  }, []);

  const selectCall = useCallback((call: RecentCall | undefined) => {
    selectedRef.current = call;
    setSelectedCallState(call);
    if (call) {
      minutesRef.current = call.minutes;
      setMinutesState(call.minutes);
    }
  }, []);

  const getCareTimeMinutes = useCallback(() => minutesRef.current, []);
  const getCareTimeCommunicationId = useCallback(
    () => selectedRef.current?.communicationId,
    [],
  );
  const getCareTimeEffectivePeriod = useCallback(() => {
    const call = selectedRef.current;
    return call ? { start: call.callTime, end: call.callEndTime } : undefined;
  }, []);

  return {
    minutes,
    setMinutes,
    selectedCall,
    selectCall,
    getCareTimeMinutes,
    getCareTimeCommunicationId,
    getCareTimeEffectivePeriod,
  };
};
